// Small shared presentational building blocks. No data fetching here.
import type { ReactNode } from 'react';
import { COPY } from '../content';
import { money } from '../format';

export function Spinner() {
  return <div className="spinner" aria-label="Loading" role="status" />;
}

export function LoadError({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="banner err">
      {message || COPY.common.loadError}
      {onRetry ? (
        <>
          {' '}
          <button className="ghost" onClick={onRetry}>
            {COPY.common.retry}
          </button>
        </>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="card center muted">{children}</div>;
}

export function Card({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <div className="card">
      {title ? <h3>{title}</h3> : null}
      {children}
    </div>
  );
}

export function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function Money({ cents, className }: { cents: number; className?: string }) {
  return <span className={`money ${className ?? ''}`}>{money(cents)}</span>;
}

type Tone = 'gray' | 'blue' | 'green' | 'amber' | 'red';
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

/** Map a ledger status to a pill tone + label. Internal; dollars live elsewhere. */
export function statusPill(status: string): ReactNode {
  const map: Record<string, { tone: Tone; label: string }> = {
    pending: { tone: 'amber', label: 'Pending' },
    held_for_review: { tone: 'amber', label: 'In review' },
    approved: { tone: 'blue', label: 'Approved' },
    payable: { tone: 'blue', label: 'Payable' },
    paid: { tone: 'green', label: 'Paid' },
    voided: { tone: 'gray', label: 'Voided' },
    denied: { tone: 'red', label: 'Denied' },
  };
  const m = map[status] ?? { tone: 'gray' as Tone, label: status };
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

export function lifecyclePill(status: string): ReactNode {
  const map: Record<string, { tone: Tone; label: string }> = {
    active: { tone: 'green', label: 'Active' },
    paused: { tone: 'amber', label: 'Paused' },
    closed: { tone: 'gray', label: 'Closed' },
  };
  const m = map[status] ?? { tone: 'gray' as Tone, label: status };
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

export function StatTile({ n, label, href }: { n: ReactNode; label: string; href?: string }) {
  const body = (
    <>
      <div className="n">{n}</div>
      <div className="l">{label}</div>
    </>
  );
  return href ? (
    <a className="stat" href={href}>
      {body}
    </a>
  ) : (
    <div className="stat">{body}</div>
  );
}

/** A dismissable toast with an optional action (used for the 10s Undo). */
export function Toast({ message, actionLabel, onAction, onClose }: { message: string; actionLabel?: string; onAction?: () => void; onClose: () => void }) {
  return (
    <div className="toast" role="status">
      <span>{message}</span>
      {actionLabel && onAction ? (
        <button
          onClick={() => {
            onAction();
            onClose();
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

/** A bottom sheet / modal. Click the scrim or Escape to close. */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div
      className="scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}
